import { Chip, Stack, Typography } from '@mui/material';
import { formatDate } from '@shared/utils/formatters.js';

/**
 * Helpers colonnes Data Grid — conventions admin Happy Paws.
 * Alignement, hiérarchie visuelle, chips statut.
 */

/** Colonne texte principale (flex, gras optionnel). */
export function colText(field, headerName, opts = {}) {
  const {
    flex = 1,
    minWidth = 140,
    width,
    fontWeight = 400,
    sortable = true,
    valueGetter,
    renderCell,
  } = opts;

  return {
    field,
    headerName,
    flex: width ? undefined : flex,
    width,
    minWidth,
    sortable,
    valueGetter,
    renderCell:
      renderCell
      || (({ value }) => (
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight, color: 'text.primary', lineHeight: 1.35 }}
        >
          {value == null || value === '' ? '—' : String(value)}
        </Typography>
      )),
  };
}

/** Colonne secondaire (texte atténué). */
export function colSecondary(field, headerName, opts = {}) {
  const { flex = 1, minWidth = 120, width, sortable = true, valueGetter } = opts;

  return {
    field,
    headerName,
    flex: width ? undefined : flex,
    width,
    minWidth,
    sortable,
    valueGetter,
    renderCell: ({ value }) => (
      <Typography variant="body2" component="span" color="text.secondary" sx={{ lineHeight: 1.35 }}>
        {value == null || value === '' ? '—' : String(value)}
      </Typography>
    ),
  };
}

/** Colonne date (alignée à droite, format FR). */
export function colDate(field, headerName, opts = {}) {
  const {
    width = 120,
    minWidth,
    sortable = true,
    withTime = false,
    valueGetter,
  } = opts;

  return {
    field,
    headerName,
    width,
    minWidth,
    sortable,
    align: 'right',
    headerAlign: 'right',
    valueGetter,
    valueFormatter: (valueOrParams) => {
      const value = (valueOrParams != null && typeof valueOrParams === 'object' && 'value' in valueOrParams)
        ? valueOrParams.value
        : valueOrParams;
      if (!value) return '—';
      if (withTime) {
        try {
          return new Intl.DateTimeFormat('fr-FR', {
            dateStyle: 'short',
            timeStyle: 'short',
          }).format(new Date(value));
        } catch {
          return '—';
        }
      }
      return formatDate(value) || '—';
    },
    renderCell: (params) => {
      const formatted = params?.formattedValue
        ?? (params?.value != null ? String(params.value) : '—');
      return (
      <Typography
        variant="body2"
        component="span"
        color="text.secondary"
        sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.35 }}
      >
        {formatted}
      </Typography>
      );
    },
  };
}

/**
 * Colonne statut avec Chip.
 * @param {Record<string, { label?: string, color?: string }>} statusMap
 */
export function colStatus(field, headerName, statusMap = {}, opts = {}) {
  const { width = 130, sortable = true } = opts;

  return {
    field,
    headerName,
    width,
    sortable,
    renderCell: ({ value }) => {
      const meta = statusMap[value] || {};
      const label = meta.label || value || '—';
      const color = meta.color || 'default';
      return (
        <Chip size="small" label={label} color={color} variant={color === 'default' ? 'outlined' : 'filled'} />
      );
    },
  };
}

/** Colonne actions (non triable, largeur fixe). */
export function colActions(renderCell, opts = {}) {
  const { width = 110, headerName = '' } = opts;
  return {
    field: 'actions',
    headerName,
    width,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell,
  };
}

/** Contact : nom principal + email secondaire empilés. */
export function colContact(opts = {}) {
  const {
    field = 'contact',
    headerName = 'Contact',
    flex = 1.2,
    minWidth = 180,
    nameField = 'contactName',
    emailField = 'contactEmail',
  } = opts;

  return {
    field,
    headerName,
    flex,
    minWidth,
    sortable: true,
    valueGetter: (value, row) => {
      const r = row || value?.row;
      return r?.[nameField] || '';
    },
    renderCell: (params) => {
      const row = params?.row || {};
      return (
      <Stack spacing={0} sx={{ py: 0.25, minWidth: 0 }}>
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
          noWrap
        >
          {row?.[nameField] || '—'}
        </Typography>
        {row?.[emailField] ? (
          <Typography variant="caption" component="span" color="text.secondary" noWrap>
            {row[emailField]}
          </Typography>
        ) : null}
      </Stack>
      );
    },
  };
}
